import { Platform } from 'react-native';

import { api, ApiError } from './api';
import { parsePositiveUserId } from './auth/mapAuthStatusUser';
import {
  BIND_AFTER_LOGIN_FLAG,
  BIND_AFTER_LOGIN_QUERY_PARAM,
  GROUPS_QUERY_KEY,
  pendingKey,
} from './bindDomainMembershipKeys';
import { impersonationService } from './impersonationService';
import { networkService } from './network';
import { storage } from './storage';
import { queryClient } from '../lib/queryClient';
import { User } from '../types/auth';

export { pendingKey } from './bindDomainMembershipKeys';
export { BIND_AFTER_LOGIN_FLAG } from './bindDomainMembershipKeys';

export type BindDomainMembershipArgs =
  | { user: User; completedLogin?: boolean }
  | { sessionOwnerUserId: number };

let lastKnownSessionOwner: number | undefined;
const inflightByOwner = new Map<number, Promise<boolean>>();
const generationByOwner = new Map<number, number>();

export function resetBindDomainMembershipForTests(): void {
  lastKnownSessionOwner = undefined;
  inflightByOwner.clear();
  generationByOwner.clear();
}

export function getLastKnownSessionOwner(): number | undefined {
  return lastKnownSessionOwner;
}

async function sessionStorageGetFlag(): Promise<string | null> {
  try {
    if (typeof sessionStorage === 'undefined') {
      return null;
    }
    return sessionStorage.getItem(BIND_AFTER_LOGIN_FLAG);
  } catch {
    return null;
  }
}

function completedLoginQueryFlagPresent(): boolean {
  try {
    return (
      typeof window !== 'undefined' &&
      new URLSearchParams(window.location.search).get(
        BIND_AFTER_LOGIN_QUERY_PARAM,
      ) === '1'
    );
  } catch {
    return false;
  }
}

export async function isAfterLoginFlagPresent(): Promise<boolean> {
  return (
    completedLoginQueryFlagPresent() || (await sessionStorageGetFlag()) === '1'
  );
}

export async function clearAfterLoginFlag(): Promise<void> {
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(BIND_AFTER_LOGIN_FLAG);
    }
  } catch {
    // Session-only marker; ignore blocked browser storage.
  }
  clearCompletedLoginQueryFlag();
}

function clearCompletedLoginQueryFlag(): void {
  try {
    if (typeof window === 'undefined' || !window.history) {
      return;
    }
    const url = new URL(window.location.href);
    if (!url.searchParams.has(BIND_AFTER_LOGIN_QUERY_PARAM)) {
      return;
    }
    url.searchParams.delete(BIND_AFTER_LOGIN_QUERY_PARAM);
    window.history.replaceState(window.history.state, '', url.toString());
  } catch {
    // The marker is best-effort and never persisted outside this URL.
  }
}

async function consumeAfterLoginFlag(): Promise<boolean> {
  const queryPresent = completedLoginQueryFlagPresent();
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(BIND_AFTER_LOGIN_FLAG);
    }
  } catch {
    if (!queryPresent) {
      return false;
    }
  }
  clearCompletedLoginQueryFlag();
  return true;
}

function isCurrentGeneration(ownerId: number, generation: number): boolean {
  return generationByOwner.get(ownerId) === generation;
}

async function setPendingIfAbsent(
  ownerId: number,
  generation?: number,
): Promise<void> {
  if (generation !== undefined && !isCurrentGeneration(ownerId, generation)) {
    return;
  }
  const existing = await storage.getItem(pendingKey(ownerId));
  if (generation !== undefined && !isCurrentGeneration(ownerId, generation)) {
    return;
  }
  if (existing == null || existing === '') {
    await storage.setItem(pendingKey(ownerId), '1');
  }
}

function isValidBindXor(body: unknown): boolean {
  if (body === null || typeof body !== 'object') {
    return false;
  }
  const rec = body as Record<string, unknown>;
  const hasGroup = rec.group !== undefined && rec.group !== null;
  const hasMembership = rec.membership !== undefined && rec.membership !== null;
  const skippedReason = rec.reason;
  const skippedBind =
    rec.skipped === true &&
    (skippedReason === 'UNPARSEABLE_HOST' ||
      skippedReason === 'INTERNAL_HOST' ||
      skippedReason === 'PUBLIC_HOST' ||
      skippedReason === 'GROUP_INACTIVE') &&
    !hasGroup &&
    !hasMembership;
  const affiliated = hasGroup && hasMembership && rec.skipped === undefined;
  return (skippedBind && !affiliated) || (affiliated && !skippedBind);
}

function isAffiliatedBindBody(body: unknown): boolean {
  if (body === null || typeof body !== 'object') {
    return false;
  }
  const rec = body as Record<string, unknown>;
  return (
    rec.group !== undefined &&
    rec.group !== null &&
    rec.membership !== undefined &&
    rec.membership !== null &&
    rec.skipped === undefined
  );
}

function pendingActionForOutcome(
  httpStatus: number,
  body: unknown,
): 'clear' | 'set' {
  if (httpStatus === 200 && isValidBindXor(body)) {
    return 'clear';
  }
  if (
    httpStatus >= 400 &&
    httpStatus < 500 &&
    httpStatus !== 429 &&
    httpStatus !== 408
  ) {
    return 'clear';
  }
  if (httpStatus === 429 || (httpStatus >= 500 && httpStatus < 600)) {
    return 'set';
  }
  return 'set';
}

function readImpersonatorUserId(session: {
  impersonator?: { user_id?: number };
}): number | undefined {
  return parsePositiveUserId(session.impersonator?.user_id);
}

export async function clearBindPendingOnLogout(
  user?: User | null,
): Promise<void> {
  let ownerId: number | undefined;
  try {
    const session = await impersonationService.getCurrentSession();
    if (session?.is_impersonating === true) {
      ownerId = readImpersonatorUserId(session) ?? lastKnownSessionOwner;
    } else if (typeof session?.is_impersonating === 'boolean') {
      ownerId = parsePositiveUserId(user?.userId);
    } else {
      ownerId = lastKnownSessionOwner;
    }
  } catch {
    ownerId = lastKnownSessionOwner;
  }
  if (ownerId !== undefined) {
    generationByOwner.set(ownerId, (generationByOwner.get(ownerId) ?? 0) + 1);
    await storage.removeItem(pendingKey(ownerId));
  }
  lastKnownSessionOwner = undefined;
}

export async function bindDomainMembershipIfNeeded(
  args: BindDomainMembershipArgs,
): Promise<void> {
  try {
    await runBind(args);
  } catch {
    // Catch all — Bind must never reject the caller.
  }
}

async function runBind(args: BindDomainMembershipArgs): Promise<void> {
  if ('user' in args && args.user.id === 'dev-bypass-user') {
    const flagPresent = await isAfterLoginFlagPresent();
    if (flagPresent) {
      await consumeAfterLoginFlag();
    }
    return;
  }

  const ownerId =
    'sessionOwnerUserId' in args
      ? parsePositiveUserId(args.sessionOwnerUserId)
      : parsePositiveUserId(args.user.userId);

  if (ownerId === undefined) {
    return;
  }

  const existing = inflightByOwner.get(ownerId);
  if (existing) {
    const generationAtWait = generationByOwner.get(ownerId) ?? 0;
    const posted = await existing;
    if (posted) {
      return;
    }
    if ((generationByOwner.get(ownerId) ?? 0) !== generationAtWait) {
      return;
    }
    return runBind(args);
  }

  const work = (async (): Promise<boolean> => {
    const flagPresent = await isAfterLoginFlagPresent();
    const completedLogin =
      'user' in args &&
      (Platform.OS === 'web' ? flagPresent : args.completedLogin === true);

    if (!completedLogin) {
      const pending = await storage.getItem(pendingKey(ownerId));
      if (pending == null || pending === '') {
        return false;
      }
    }

    const generation = (generationByOwner.get(ownerId) ?? 0) + 1;
    generationByOwner.set(ownerId, generation);

    let session: Awaited<
      ReturnType<typeof impersonationService.getCurrentSession>
    > | null = null;
    let sessionFailed = false;
    try {
      session = await impersonationService.getCurrentSession();
      if (session == null || typeof session.is_impersonating !== 'boolean') {
        sessionFailed = true;
      }
    } catch {
      sessionFailed = true;
    }

    if (sessionFailed) {
      if (completedLogin) {
        if (Platform.OS === 'web') {
          return false;
        }
        if (isCurrentGeneration(ownerId, generation)) {
          lastKnownSessionOwner = lastKnownSessionOwner ?? ownerId;
          await setPendingIfAbsent(ownerId, generation);
        }
      }
      return false;
    }

    if (session?.is_impersonating === true) {
      const impersonatorId = readImpersonatorUserId(session);
      if (impersonatorId === undefined) {
        return false;
      }
      if (isCurrentGeneration(ownerId, generation)) {
        lastKnownSessionOwner = impersonatorId;
        if (completedLogin) {
          if (Platform.OS === 'web') {
            const consumed = await consumeAfterLoginFlag();
            if (!consumed) {
              return false;
            }
          }
          await setPendingIfAbsent(impersonatorId);
        }
      }
      return false;
    }

    if (isCurrentGeneration(ownerId, generation)) {
      lastKnownSessionOwner = ownerId;
    }

    if (completedLogin && Platform.OS === 'web') {
      const consumed = await consumeAfterLoginFlag();
      if (!consumed) {
        return false;
      }
    }

    if (!completedLogin) {
      if (!networkService.isInitialized()) {
        try {
          await networkService.initialize();
        } catch {
          return false;
        }
      }
      if (!networkService.isInitialized() || !networkService.isConnected()) {
        return false;
      }
    } else if (!networkService.isInitialized()) {
      try {
        await networkService.initialize();
      } catch {
        await setPendingIfAbsent(ownerId, generation);
        return false;
      }
      if (!networkService.isInitialized() || !networkService.isConnected()) {
        await setPendingIfAbsent(ownerId, generation);
        return false;
      }
    } else if (!networkService.isConnected()) {
      await setPendingIfAbsent(ownerId, generation);
      return false;
    }

    if (!isCurrentGeneration(ownerId, generation)) {
      return false;
    }

    try {
      const body = await api.post<unknown>('/groups:bindDomainMembership', {});
      if (!isCurrentGeneration(ownerId, generation)) {
        return false;
      }
      await applyPendingOutcome(ownerId, 200, body, generation);
    } catch (error) {
      if (!isCurrentGeneration(ownerId, generation)) {
        return false;
      }
      const status = error instanceof ApiError ? error.status : 0;
      await applyPendingOutcome(ownerId, status, {}, generation);
    }
    return true;
  })();

  inflightByOwner.set(ownerId, work);
  try {
    await work;
  } finally {
    if (inflightByOwner.get(ownerId) === work) {
      inflightByOwner.delete(ownerId);
    }
  }
}

async function applyPendingOutcome(
  ownerId: number,
  httpStatus: number,
  body: unknown,
  generation: number,
): Promise<void> {
  if (!isCurrentGeneration(ownerId, generation)) {
    return;
  }
  const action = pendingActionForOutcome(httpStatus, body);
  if (action === 'clear') {
    await storage.removeItem(pendingKey(ownerId));
    if (
      httpStatus === 200 &&
      isAffiliatedBindBody(body) &&
      isCurrentGeneration(ownerId, generation)
    ) {
      void queryClient
        .invalidateQueries({
          queryKey: GROUPS_QUERY_KEY,
          refetchType: 'active',
        })
        .catch(() => undefined);
    }
    return;
  }
  await setPendingIfAbsent(ownerId, generation);
}
